import * as THREE from 'three'
import type {
  GraphicsBackend,
  GraphicsRegionCaptureSession,
  ReadonlyGraphicsInfo,
} from '@/graphics/GraphicsBackend'
import { BackendMismatchError, UnsupportedGraphicsCapabilityError } from '@/graphics/GraphicsErrors'
import { createViewScopedDomElement } from '@/plugins/control/createViewScopedDomElement'
import type {
  BoundingBoxOverlayPlugin,
  DynamicBoundingBoxHighlightEntry,
  StaticBoundingBoxEntry,
} from '@/plugins/pointCloud/BoundingBoxOverlayPlugin'
import type { PointCloudRenderConfig } from '@/plugins/pointCloud/types'
import type { MeasureResult, MeasureToolSessionOptions } from '@/plugins/tools/measure/MeasureData'
import type { ProfileResult } from '@/plugins/tools/profile/ProfileData'
import type { EngineContext } from '@/runtime/EngineContext'
import type { EventBus } from '@/runtime/EventBus'
import type { PluginManager, PluginQuery } from '@/runtime/PluginManager'
import type { RenderLoop } from '@/runtime/RenderLoop'
import type { SceneGraphService } from '@/runtime/SceneGraphService'
import type { UnitPresentationService } from '@/runtime/UnitPresentationService'
import type { ILogger, ILogSink, LogLevelName } from '@/shared/logging'
import { getLoggerManager, LogLevel } from '@/shared/logging'
import type {
  AssetHandle,
  ClipDraftState,
  Configurable,
  Disposable,
  PickQuery,
  PickResult,
  ToolSession,
} from '@/shared/types/core'
import type { ActivationToken } from '@/shared/types/plugin'
import type {
  CadHandle,
  CadLoadRequest,
  CameraRig,
  CharacterService,
  ControlManager,
  FitOptions,
  GaussianSplatHandle,
  GaussianSplatLoadRequest,
  InputRouter,
  InteractionService,
  PickingService,
  PointCloudHandle,
  PointCloudLoadRequest,
  PointCloudService,
  RenderPipeline,
  SelectionService,
  ServiceMap,
  SpatialQueryService,
  ViewportCamera,
  ViewRegistry,
  ViewScopedServiceMap,
} from '@/shared/types/services'
import type {
  ViewCreateOptions,
  ViewExtensionKey,
  ViewHandle,
  ViewId,
  ViewRect,
  ViewSnapshot,
  ViewUpdateOptions,
} from '@/shared/types/view'
import { normalizeScreenshotMimeType, type ScreenshotOptions } from './screenshot'
import { encodeCanvasInWorker, supportsScreenshotWorkerEncoding } from './screenshotWorker'
import type { EngineViews } from './types'

type ViewRegistryGetter = () => ViewRegistry
type ViewScopedServiceKey = keyof ViewScopedServiceMap
type ViewHostHelpers = {
  worldToScreen: (id: ViewId, point: THREE.Vector3) => THREE.Vector2
  fitToScene: (id: ViewId, options?: FitOptions) => boolean
  fitToAssets: (id: ViewId, ids: string[], options?: FitOptions) => boolean
  pickAtScreen: (id: ViewId, screen: THREE.Vector2, options?: Partial<PickQuery>) => PickResult[]
  pickAtScreenAsync: (
    id: ViewId,
    screen: THREE.Vector2,
    options?: Partial<PickQuery>
  ) => Promise<PickResult[]>
  createScopedDomElement: (id: ViewId, source: HTMLElement) => HTMLElement
}

const VIEW_SCOPED_SERVICE_PLUGIN_MAP: Record<ViewScopedServiceKey, string> = {
  cameraRig: 'CameraPlugin',
  viewportCamera: 'CameraPlugin',
  controlManager: 'ControlPlugin',
}

const memoryLog = getLoggerManager().getLogger('runtime:memory')

function normalizeScreenshotDimension(value: number | undefined, fallback: number): number {
  const candidate = value ?? fallback
  return Number.isFinite(candidate) && candidate > 0 ? Math.max(1, Math.round(candidate)) : 1
}

function runMemoryDiagnostic(write: () => void): void {
  try {
    write()
  } catch {
    // 诊断失败不得改变引擎生命周期语义。
  }
}

class EngineViewHandleImpl implements ViewHandle {
  readonly id: ViewId

  private readonly getViewRegistry: ViewRegistryGetter

  constructor(id: ViewId, getViewRegistry: ViewRegistryGetter) {
    this.id = id
    this.getViewRegistry = getViewRegistry
  }

  getSnapshot(): ViewSnapshot {
    const snapshot = this.getViewRegistry().get(this.id)
    if (!snapshot) {
      throw new Error(`View "${this.id}" does not exist.`)
    }
    return snapshot
  }

  update(patch: ViewUpdateOptions): ViewSnapshot {
    return this.getViewRegistry().update(this.id, patch)
  }

  setRect(rect: ViewRect): ViewSnapshot {
    return this.getViewRegistry().setRect(this.id, rect)
  }

  setVisible(visible: boolean): ViewSnapshot {
    return this.getViewRegistry().setVisible(this.id, visible)
  }

  setZIndex(zIndex: number): ViewSnapshot {
    return this.getViewRegistry().setZIndex(this.id, zIndex)
  }

  setLayerMask(layerMask: number): ViewSnapshot {
    return this.getViewRegistry().setLayerMask(this.id, layerMask)
  }

  setUserData(userData?: Record<string, unknown>): ViewSnapshot {
    return this.getViewRegistry().update(this.id, { userData: userData ?? {} })
  }

  getCameraRig(): CameraRig | undefined {
    return this.getViewService('cameraRig')
  }

  requireCameraRig(): CameraRig {
    return this.requireViewService('cameraRig')
  }

  getViewportCamera(): ViewportCamera | undefined {
    return this.getViewService('viewportCamera')
  }

  requireViewportCamera(): ViewportCamera {
    return this.requireViewService('viewportCamera')
  }

  getControlManager(): ControlManager | undefined {
    return this.getViewService('controlManager')
  }

  requireControlManager(): ControlManager {
    return this.requireViewService('controlManager')
  }

  getScopedServices(): Partial<ViewScopedServiceMap> {
    this.getSnapshot()
    const scopedServices: Partial<ViewScopedServiceMap> = {}
    const cameraRig = this.getViewService('cameraRig')
    const viewportCamera = this.getViewService('viewportCamera')
    const controlManager = this.getViewService('controlManager')

    if (cameraRig) {
      scopedServices.cameraRig = cameraRig
    }
    if (viewportCamera) {
      scopedServices.viewportCamera = viewportCamera
    }
    if (controlManager) {
      scopedServices.controlManager = controlManager
    }

    return scopedServices
  }

  getSlot<T = unknown>(name: string): T | undefined {
    return this.getViewRegistry().getExtension(this.id, name as ViewExtensionKey) as T | undefined
  }

  setSlot(name: string, value: unknown): void {
    this.getViewRegistry().setExtension(this.id, name as ViewExtensionKey, value as never)
  }

  clearSlot(name: string): void {
    this.getViewRegistry().setExtension(this.id, name as ViewExtensionKey, undefined as never)
  }

  private getViewService<K extends ViewScopedServiceKey>(
    name: K
  ): ViewScopedServiceMap[K] | undefined {
    this.getSnapshot()
    return this.getViewRegistry().getExtension(this.id, name as ViewExtensionKey) as
      | ViewScopedServiceMap[K]
      | undefined
  }

  private requireViewService<K extends ViewScopedServiceKey>(name: K): ViewScopedServiceMap[K] {
    const service = this.getViewService(name)
    if (!service) {
      throw new Error(
        `View "${this.id}" does not expose ${name}. Ensure ${VIEW_SCOPED_SERVICE_PLUGIN_MAP[name]} is registered and the view has been initialized.`
      )
    }
    return service
  }
}

class EngineViewsImpl implements EngineViews {
  private readonly getViewRegistry: ViewRegistryGetter
  private readonly createViewHandle: (id: ViewId) => ViewHandle
  private readonly helpers: ViewHostHelpers

  constructor(
    getViewRegistry: ViewRegistryGetter,
    createViewHandle: (id: ViewId) => ViewHandle,
    helpers: ViewHostHelpers
  ) {
    this.getViewRegistry = getViewRegistry
    this.createViewHandle = createViewHandle
    this.helpers = helpers
  }

  get primaryViewId(): ViewId {
    return this.getViewRegistry().primaryViewId
  }

  create(options: ViewCreateOptions): ViewHandle {
    const snapshot = this.getViewRegistry().create(options)
    return this.createViewHandle(snapshot.id)
  }

  remove(id: ViewId): boolean {
    return this.getViewRegistry().remove(id)
  }

  get(id: ViewId): ViewHandle | undefined {
    const snapshot = this.getViewRegistry().get(id)
    return snapshot ? this.createViewHandle(snapshot.id) : undefined
  }

  list(): ViewHandle[] {
    return this.getViewRegistry()
      .list()
      .map((view) => this.createViewHandle(view.id))
  }

  getActive(): ViewHandle | undefined {
    const snapshot = this.getViewRegistry().getActive()
    return snapshot ? this.createViewHandle(snapshot.id) : undefined
  }

  setActive(id: ViewId): ViewHandle {
    const snapshot = this.getViewRegistry().setActive(id)
    return this.createViewHandle(snapshot.id)
  }

  update(id: ViewId, patch: ViewUpdateOptions): ViewHandle {
    const snapshot = this.getViewRegistry().update(id, patch)
    return this.createViewHandle(snapshot.id)
  }

  setRect(id: ViewId, rect: ViewRect): ViewHandle {
    const snapshot = this.getViewRegistry().setRect(id, rect)
    return this.createViewHandle(snapshot.id)
  }

  setVisible(id: ViewId, visible: boolean): ViewHandle {
    const snapshot = this.getViewRegistry().setVisible(id, visible)
    return this.createViewHandle(snapshot.id)
  }

  setLayerMask(id: ViewId, layerMask: number): ViewHandle {
    const snapshot = this.getViewRegistry().setLayerMask(id, layerMask)
    return this.createViewHandle(snapshot.id)
  }

  setZIndex(id: ViewId, zIndex: number): ViewHandle {
    const snapshot = this.getViewRegistry().setZIndex(id, zIndex)
    return this.createViewHandle(snapshot.id)
  }

  getCameraRig(id: ViewId): CameraRig | undefined {
    return this.get(id)?.getCameraRig()
  }

  requireCameraRig(id: ViewId): CameraRig {
    return this.requireViewHandle(id).requireCameraRig()
  }

  getViewportCamera(id: ViewId): ViewportCamera | undefined {
    return this.get(id)?.getViewportCamera()
  }

  requireViewportCamera(id: ViewId): ViewportCamera {
    return this.requireViewHandle(id).requireViewportCamera()
  }

  getControlManager(id: ViewId): ControlManager | undefined {
    return this.get(id)?.getControlManager()
  }

  requireControlManager(id: ViewId): ControlManager {
    return this.requireViewHandle(id).requireControlManager()
  }

  getScopedServices(id: ViewId): Partial<ViewScopedServiceMap> | undefined {
    return this.get(id)?.getScopedServices()
  }

  worldToScreen(id: ViewId, point: THREE.Vector3): THREE.Vector2 {
    this.requireViewHandle(id)
    return this.helpers.worldToScreen(id, point)
  }

  fitToScene(id: ViewId, options?: FitOptions): boolean {
    this.requireViewHandle(id)
    return this.helpers.fitToScene(id, options)
  }

  fitToAssets(id: ViewId, ids: string[], options?: FitOptions): boolean {
    this.requireViewHandle(id)
    return this.helpers.fitToAssets(id, ids, options)
  }

  pickAtScreen(id: ViewId, screen: THREE.Vector2, options: Partial<PickQuery> = {}): PickResult[] {
    this.requireViewHandle(id)
    return this.helpers.pickAtScreen(id, screen, options)
  }

  pickAtScreenAsync(
    id: ViewId,
    screen: THREE.Vector2,
    options: Partial<PickQuery> = {}
  ): Promise<PickResult[]> {
    this.requireViewHandle(id)
    return this.helpers.pickAtScreenAsync(id, screen, options)
  }

  createScopedDomElement(id: ViewId, source: HTMLElement): HTMLElement {
    this.requireViewHandle(id)
    return this.helpers.createScopedDomElement(id, source)
  }

  private requireViewHandle(id: ViewId): ViewHandle {
    const viewHandle = this.get(id)
    if (!viewHandle) {
      throw new Error(`View "${id}" does not exist.`)
    }
    return viewHandle
  }
}

/**
 * 引擎骨架 —— 宿主应用的主入口
 *
 * 持有 EngineContext / PluginManager / RenderLoop，
 * 向宿主暴露 PluginQuery 只读查询和生命周期控制。
 * 后续 Phase 3-5 逐步添加便捷访问器。
 */
export class PotreeEngine implements Disposable {
  /** 引擎上下文（基础设施 + 服务注册中心） */
  readonly context: EngineContext
  /** 只读 Plugin 查询接口 */
  readonly plugins: PluginQuery
  /** 事件总线 */
  readonly events: EventBus
  /** 帧循环管理器 */
  readonly loop: RenderLoop
  /** Plugin 管理器（内部使用，不对宿主暴露） */
  private readonly pluginManager: PluginManager
  /** 正式图形资源 owner；旧测试/手工构造路径可暂时缺省。 */
  private readonly graphicsBackend: GraphicsBackend | null
  /** 工具激活令牌，由构造时创建并注入所有 ToolPlugin */
  private readonly activationToken!: ActivationToken
  /** 当前各 group 的活跃工具：group → tool name */
  private readonly activeTools = new Map<string, string>()
  /** 当前各 group 的活跃 session，用于生命周期同步 */
  private readonly activeSessions = new Map<string, { invalidate: () => void }>()
  private readonly viewsApi: EngineViews

  /** 引擎是否就绪 */
  private _ready = false
  /** 引擎是否已销毁 */
  private _disposed = false
  /** 仅用于把同一实例的创建、销毁阶段日志串联起来 */
  private readonly memoryDiagnosticEngineId: number
  /** 引擎是否已完成初始化并可用 */
  get ready(): boolean {
    return this._ready
  }

  /** 引擎是否已调用 dispose */
  get disposed(): boolean {
    return this._disposed
  }

  constructor(params: {
    context: EngineContext
    plugins: PluginQuery
    events: EventBus
    loop: RenderLoop
    pluginManager: PluginManager
  }) {
    this.context = params.context
    this.memoryDiagnosticEngineId = params.context.memoryDiagnosticId
    this.plugins = params.plugins
    this.events = params.events
    this.loop = params.loop
    this.pluginManager = params.pluginManager
    this.graphicsBackend = params.context.graphics
    this.activationToken = Symbol('potree-activation-token')
    const getViewRegistry = (): ViewRegistry =>
      this.requireService('ViewRegistry', 'views', 'ViewPlugin')
    this.viewsApi = new EngineViewsImpl(
      getViewRegistry,
      (id) => new EngineViewHandleImpl(id, getViewRegistry),
      {
        worldToScreen: (id, point) => this.worldToViewScreen(id, point),
        fitToScene: (id, options) => this.fitViewToScene(id, options),
        fitToAssets: (id, ids, options) => this.fitViewToAssets(id, ids, options),
        pickAtScreen: (id, screen, options) => this.pickAtViewScreen(id, screen, options),
        pickAtScreenAsync: (id, screen, options) => this.pickAtViewScreenAsync(id, screen, options),
        createScopedDomElement: (id, source) => this.createScopedDomElementForView(id, source),
      }
    )
    for (const tool of this.pluginManager.getToolPlugins()) {
      tool.setActivationToken(this.activationToken)
    }

    // 监听插件自退出请求，走正规 deactivateTool 流程以同步 activeTools + emit tool.deactivated
    this.events.on('tool.requestDeactivate', ({ group }) => {
      this.deactivateTool(group)
    })

    this._installGlobalLogControl()
  }

  /** 挂载 globalThis.__SHARE3D_LOG__，供生产环境控制台手动开启日志（绕过 SILENT 默认） */
  private _installGlobalLogControl(): void {
    const mgr = getLoggerManager()
    ;(globalThis as { __SHARE3D_LOG__?: unknown }).__SHARE3D_LOG__ = {
      setLevel: (level: LogLevelName) => mgr.setLevel(level),
      setNamespaceLevel: (ns: string, level: LogLevelName) => mgr.setNamespaceLevel(ns, level),
      setPerfEnabled: (enabled: boolean) => mgr.setPerfEnabled(enabled),
    }
  }

  /** 标记引擎就绪（由 createPotreeEngine 调用） */
  markReady(): void {
    this.assertNotDisposed()
    this._ready = true
    this._logMemorySummary('engine.ready')
  }

  /** 默认 group 当前活跃工具名，无活跃工具时返回 null */
  get activeTool(): string | null {
    return this.activeTools.get('default') ?? null
  }

  /**
   * 获取指定 group 的活跃工具名。
   *
   * @param group - 工具分组名，默认 'default'
   * @returns 活跃工具名，无活跃工具返回 null
   */
  getActiveTool(group: string = 'default'): string | null {
    return this.activeTools.get(group) ?? null
  }

  /**
   * 激活指定工具，同 group 内自动互斥。
   *
   * @param name - 目标工具名，需已注册为 ToolPlugin
   */
  activateTool(name: string): void {
    this.assertNotDisposed()
    const toolPlugins = this.pluginManager.getToolPlugins()
    const target = toolPlugins.find((t) => t.name === name)
    if (!target) {
      throw new Error(`未找到 ToolPlugin: "${name}"，请检查是否已注册`)
    }
    const group = target.group ?? 'default'
    const currentName = this.activeTools.get(group)
    if (currentName === name && target.isActive) return
    if (currentName) {
      const current = toolPlugins.find((t) => t.name === currentName)
      if (current?.isActive) {
        current.deactivate(this.activationToken)
        this.events.emit('tool.deactivated', { name: currentName, group })
      }
      this.activeTools.delete(group)
    }
    target.activate(this.activationToken)
    this.activeTools.set(group, name)
    this.events.emit('tool.activated', { name, group })
  }

  /**
   * 停用指定 group 的活跃工具，默认停用 'default' 组。
   *
   * @param group - 目标工具分组，未传入时使用 'default'
   */
  deactivateTool(group?: string): void {
    this.assertNotDisposed()
    const resolvedGroup = group ?? 'default'
    const currentName = this.activeTools.get(resolvedGroup)
    if (!currentName) return
    const current = this.pluginManager.getToolPlugins().find((t) => t.name === currentName)
    if (current?.isActive) {
      current.deactivate(this.activationToken)
      this.events.emit('tool.deactivated', { name: currentName, group: resolvedGroup })
    }
    this.activeTools.delete(resolvedGroup)
  }

  /**
   * 创建工具会话 —— activateTool 的增强入口
   * 可预配置工具参数，订阅结果变更，统一 cancel 控制。
   *
   * 生命周期同步保证：
   * - 外部调 deactivateTool() 或 activateTool() 互斥掉当前工具时，session 自动失效
   * - 同 group 重复调 startToolSession() 时，旧 session 自动失效
   * - 引擎 dispose 时，所有 session 自动失效
   */
  /** 启动测量工具会话 */
  startToolSession(
    name: 'measure',
    options?: MeasureToolSessionOptions
  ): ToolSession<MeasureResult[]>
  /** 启动裁切工具会话 */
  startToolSession(
    name: 'clip',
    options?: { selectionMode?: string; clipApplyMode?: string }
  ): ToolSession<ClipDraftState>
  /** 启动剖面工具会话 */
  startToolSession(name: 'profile', options?: Record<string, unknown>): ToolSession<ProfileResult[]>
  /** 启动任意工具会话 */
  startToolSession(name: string, options?: Record<string, unknown>): ToolSession
  /**
   * 启动工具会话，ativateTool 的增强入口。
   *
   * 可预配置工具参数、订阅结果变更并统一 cancel 控制，会话生命周期与工具激活状态同步。
   *
   * @param name - 目标工具名
   * @param options - 工具预配置参数
   * @returns 会话句柄，支持 cancel / onResult / dispose
   */
  startToolSession(name: string, options?: Record<string, unknown>): ToolSession {
    this.assertNotDisposed()

    const target = this.pluginManager.getToolPlugins().find((tool) => tool.name === name)
    const group = target?.group ?? 'default'
    // 淘汰同 group 的旧 session（须在预配置前失效，避免旧会话收到新会话的配置事件）
    const previousSession = target ? this.activeSessions.get(group) : undefined
    previousSession?.invalidate()

    // 查找并预配置
    this._configureToolBeforeActivation(name, options)

    // 激活工具（内部会互斥掉同 group 旧工具，触发 tool.deactivated）
    this.activateTool(name)

    let disposed = false
    const resultHandlers = new Set<(result: unknown) => void>()

    /** 清理所有监听器，标记为已失效 */
    const cleanup = () => {
      if (disposed) return
      disposed = true
      this.events.off('tool.resultChanged', onResult)
      this.events.off('tool.deactivated', onDeactivated)
      resultHandlers.clear()
      // 从 activeSessions 中移除（仅当自己仍是当前 session 时）
      if (this.activeSessions.get(group)?.invalidate === cleanup) {
        this.activeSessions.delete(group)
      }
    }

    // 监听 tool.resultChanged 事件，分发给订阅者
    const onResult = (data: { name: string; data: unknown }) => {
      if (disposed || data.name !== name) return
      for (const handler of resultHandlers) {
        handler(data.data)
      }
    }

    // 监听 tool.deactivated 事件，外部停用时自动清理
    const onDeactivated = (data: { name: string; group: string }) => {
      if (data.name === name && data.group === group) {
        cleanup()
      }
    }

    this.events.on('tool.resultChanged', onResult)
    this.events.on('tool.deactivated', onDeactivated)

    // 注册当前 session
    this.activeSessions.set(group, { invalidate: cleanup })

    const session: ToolSession = {
      name,
      group,
      get active() {
        return !disposed
      },
      cancel: () => {
        if (disposed) return
        cleanup()
        // 仅当工具仍然激活时才停用（可能已被外部停用）
        if (this.activeTools.get(group) === name) {
          this.deactivateTool(group)
        }
      },
      onResult: (cb: (result: unknown) => void): Disposable => {
        resultHandlers.add(cb)
        return {
          dispose: () => resultHandlers.delete(cb),
        }
      },
      dispose: () => {
        session.cancel()
      },
    }

    return session
  }

  /** 工具激活前的预配置 */
  private _configureToolBeforeActivation(name: string, options?: Record<string, unknown>): void {
    if (name === 'measure' && this.context.hasService('MeasureService')) {
      const svc = this.context.getService('MeasureService')
      const measureOptions = options as MeasureToolSessionOptions | undefined
      svc.setCoordinatePreviewLabel(undefined)
      svc.setUnitPreferenceResolver(measureOptions?.unitPreferenceResolver)
      svc.setAreaPlaneConstraintEnabled(measureOptions?.areaPlaneConstraintEnabled ?? true)
      if (measureOptions?.measureType !== undefined) {
        svc.setMeasureType(measureOptions.measureType)
      }
      svc.setCoordinateMode(measureOptions?.coordinateMode ?? 'single')
    }

    if (!options) return

    if (name === 'clip' && this.context.hasService('ClipService')) {
      const svc = this.context.getService('ClipService')
      if (options.selectionMode !== undefined) {
        svc.setSelectionMode(options.selectionMode as any)
      }
      if (options.clipApplyMode !== undefined) {
        svc.setClipApplyMode(options.clipApplyMode as any)
      }
    }
  }

  // ==================== 便捷服务访问器 ====================

  /** 相机装置（缺失 CameraPlugin 时抛错） */
  get views(): EngineViews {
    this.requireService('ViewRegistry', 'views', 'ViewPlugin')
    return this.viewsApi
  }

  /** 主视口的相机装置（缺失 CameraPlugin 时抛错） */
  get camera(): CameraRig {
    return this.requireService('CameraRig', 'camera', 'CameraPlugin')
  }

  /** 控制器管理器（缺失 ControlPlugin 时抛错） */
  get controls(): ControlManager {
    return this.requireService('ControlManager', 'controls', 'ControlPlugin')
  }

  /** 角色服务（缺失 CharacterPlugin 时抛错） */
  get characters(): CharacterService {
    return this.requireService('CharacterService', 'characters', 'CharacterPlugin')
  }

  /** 3D Tiles 服务（缺失 TilesPlugin 时抛错） */
  get tiles(): import('@/plugins/tiles/types').ITilesService {
    return this.requireService('TilesService', 'tiles', 'TilesPlugin')
  }

  /** 输入路由（缺失 InputPlugin 时抛错） */
  get input(): InputRouter {
    return this.requireService('InputRouter', 'input', 'InputPlugin')
  }

  /** 拾取服务（缺失 PickingPlugin 时抛错） */
  get picking(): PickingService {
    return this.requireService('PickingService', 'picking', 'PickingPlugin')
  }

  /** 空间查询服务（缺失 SpatialQueryPlugin 时抛错） */
  get spatial(): SpatialQueryService {
    return this.requireService('SpatialQueryService', 'spatial', 'SpatialQueryPlugin')
  }

  /** 选择服务（缺失 SelectionPlugin 时抛错） */
  get selection(): SelectionService {
    return this.requireService('SelectionService', 'selection', 'SelectionPlugin')
  }

  /** 渲染管线（缺失 RenderPlugin 时抛错） */
  get pipeline(): RenderPipeline {
    return this.requireService('RenderPipeline', 'pipeline', 'RenderPlugin')
  }

  /** 场景图（runtime 内建，始终可用） */
  get sceneGraph(): SceneGraphService {
    this.assertNotDisposed()
    return this.context.sceneGraph
  }

  /** 实例级显示单位服务。 */
  get unitPresentation(): UnitPresentationService {
    this.assertNotDisposed()
    return this.context.unitPresentation
  }

  /** 当前活动相机（缺失 CameraPlugin 时抛错） */
  get activeCamera(): THREE.Camera {
    return this.requireService<'ViewportCamera'>(
      'ViewportCamera',
      'activeCamera',
      'CameraPlugin'
    ).getActiveCamera()
  }

  /** 交互服务（缺失 InteractionPlugin 时抛错） */
  get interaction(): InteractionService {
    return this.requireService('InteractionService', 'interaction', 'InteractionPlugin')
  }

  /** 点云服务（缺失 PointCloudPlugin 时抛错）。 */
  get pointClouds(): PointCloudService {
    return this.requireService('PointCloudService', 'pointClouds', 'PointCloudPlugin')
  }

  // ==================== 点云便捷方法 ====================

  /**
   * 设置 EDL 渲染参数。
   *
   * @param enabled - 是否启用 EDL 后处理
   * @param options - EDL 细节参数，包含 strength/radius/opacity
   */
  setEDL(
    enabled: boolean,
    options?: { strength?: number; radius?: number; opacity?: number }
  ): void {
    this.assertNotDisposed()
    this.pointClouds.setEDL(enabled, options)
  }

  /**
   * 设置是否显示点云包围盒。
   *
   * @param visible - 是否显示包围盒
   */
  setShowBoundingBox(visible: boolean): void {
    this.assertNotDisposed()
    const plugin = this.pluginManager.get('pointCloud')
    if (!plugin) {
      throw new Error('setShowBoundingBox requires PointCloudPlugin')
    }
    const configurable = plugin as unknown as Configurable<PointCloudRenderConfig>
    const current = configurable.getConfig()
    configurable.setConfig({ ...current, showBoundingBox: visible })
  }

  /**
   * 高亮显示指定点云的包围盒线框（世界 AABB），支持单/多选。
   *
   * @param ids - 需要高亮的点云资产 id 或高亮条目数组；传空数组清除全部高亮
   */
  setBoundingBoxHighlight(ids: ReadonlyArray<string | DynamicBoundingBoxHighlightEntry>): void {
    this.assertNotDisposed()
    const plugin = this.pluginManager.get('boundingBoxOverlay')
    if (!plugin) {
      throw new Error('setBoundingBoxHighlight requires BoundingBoxOverlayPlugin')
    }
    ;(plugin as unknown as BoundingBoxOverlayPlugin).setHighlightedIds(ids)
  }

  /**
   * 高亮显示【未加载】点云的静态包围盒线框（直接给世界 AABB，来自 metadata.json）。
   *
   * @param boxes - 静态包围盒条目数组；传空数组清除全部静态盒
   */
  setStaticBoundingBoxHighlight(boxes: ReadonlyArray<StaticBoundingBoxEntry>): void {
    this.assertNotDisposed()
    const plugin = this.pluginManager.get('boundingBoxOverlay')
    if (!plugin) {
      throw new Error('setStaticBoundingBoxHighlight requires BoundingBoxOverlayPlugin')
    }
    ;(plugin as unknown as BoundingBoxOverlayPlugin).setStaticBoxes(boxes)
  }

  /**
   * 将世界坐标转换为主视口屏幕坐标。
   *
   * @param point - 世界坐标点
   * @returns 屏幕坐标，单位 CSS 像素
   */
  worldToScreen(point: THREE.Vector3): THREE.Vector2 {
    this.assertNotDisposed()
    const camera = this.activeCamera
    const surface = this.context.graphics.surface
    const primaryRect = this.context.hasService('ViewRegistry')
      ? this.context
          .getService('ViewRegistry')
          .get(this.context.getService('ViewRegistry').primaryViewId)?.rect
      : undefined
    const rect = primaryRect ?? {
      x: 0,
      y: 0,
      width: surface.logicalWidth,
      height: surface.logicalHeight,
    }

    const projected = point.clone().project(camera)
    return new THREE.Vector2(
      rect.x + (projected.x + 1) * 0.5 * rect.width,
      rect.y + (-projected.y + 1) * 0.5 * rect.height
    )
  }

  // ==================== 便捷资产加载 ====================

  /**
   * 加载点云资产，内部依次执行 load 与 mount。
   *
   * @param request - 点云加载请求
   * @returns 已挂载的点云句柄
   */
  async loadPointCloud(request: PointCloudLoadRequest): Promise<PointCloudHandle> {
    this.assertNotDisposed()
    const service = this.context.getService('PointCloudService')
    const handle = await service.load(request)
    try {
      this.assertNotDisposed()
    } catch (error) {
      this.disposeLateLoadedAsset(service, handle)
      throw error
    }
    try {
      service.mount(handle)
    } catch (error) {
      this.disposeLateLoadedAsset(service, handle)
      throw error
    }
    return handle
  }

  /**
   * 加载 3DGS 资产，内部依次执行 load 与 mount。
   *
   * @param request - 3DGS 加载请求
   * @returns 已挂载的 3DGS 句柄
   */
  async loadGaussianSplat(request: GaussianSplatLoadRequest): Promise<GaussianSplatHandle> {
    this.assertNotDisposed()
    const service = this.context.getService('GaussianSplatService')
    const handle = await service.load(request)
    try {
      this.assertNotDisposed()
    } catch (error) {
      this.disposeLateLoadedAsset(service, handle)
      throw error
    }
    try {
      service.mount(handle)
    } catch (error) {
      this.disposeLateLoadedAsset(service, handle)
      throw error
    }
    return handle
  }

  /**
   * 加载 CAD 场景，内部依次执行 load 与 mount。
   *
   * @param request - CAD 加载请求
   * @returns 已挂载的 CAD 句柄
   */
  async loadCadScene(request: CadLoadRequest): Promise<CadHandle> {
    this.assertNotDisposed()
    const service = this.context.getService('CadService')
    const handle = await service.load(request)
    try {
      this.assertNotDisposed()
    } catch (error) {
      this.disposeLateLoadedAsset(service, handle)
      throw error
    }
    try {
      service.mount(handle)
    } catch (error) {
      this.disposeLateLoadedAsset(service, handle)
      throw error
    }
    return handle
  }

  // ==================== 便捷控制方法 ====================

  /** 适配相机使所有资产完整可见 */
  fitAll(): void {
    this.assertNotDisposed()
    const box = this.context.sceneBounds.getWorldBoundingBox({ purpose: 'fit' })
    if (!box.isEmpty()) {
      this.camera.fitToBox(box)
    }
  }

  /**
   * 适配相机使指定资产完整可见。
   *
   * @param ids - 参与拟合的资产 ID 列表
   */
  fitToAssets(ids: string[]): void {
    this.assertNotDisposed()
    const box = this.context.sceneBounds.getWorldBoundingBox({
      ids,
      includeInvisible: true,
      purpose: 'fit',
    })
    if (!box.isEmpty()) {
      this.camera.fitToBox(box)
    }
  }

  private worldToViewScreen(viewId: ViewId, point: THREE.Vector3): THREE.Vector2 {
    this.assertNotDisposed()
    const view = this.views.get(viewId)
    if (!view) {
      throw new Error(`View "${viewId}" does not exist.`)
    }

    const { rect } = view.getSnapshot()
    const camera = this.views.requireViewportCamera(viewId).getActiveCamera()
    camera.updateMatrixWorld(true)

    const projected = point.clone().project(camera)
    return new THREE.Vector2(
      (projected.x + 1) * 0.5 * rect.width,
      (-projected.y + 1) * 0.5 * rect.height
    )
  }

  private fitViewToScene(viewId: ViewId, options?: FitOptions): boolean {
    this.assertNotDisposed()
    const box = this.context.sceneBounds.getWorldBoundingBox({ purpose: 'fit' })
    if (box.isEmpty()) {
      return false
    }

    this.views.requireCameraRig(viewId).fitToBox(box, options)
    return true
  }

  private fitViewToAssets(viewId: ViewId, ids: string[], options?: FitOptions): boolean {
    this.assertNotDisposed()
    const box = this.context.sceneBounds.getWorldBoundingBox({
      ids,
      includeInvisible: true,
      purpose: 'fit',
    })
    if (box.isEmpty()) {
      return false
    }

    this.views.requireCameraRig(viewId).fitToBox(box, options)
    return true
  }

  private pickAtViewScreen(
    viewId: ViewId,
    screen: THREE.Vector2,
    options: Partial<PickQuery> = {}
  ): PickResult[] {
    this.assertNotDisposed()
    return this.picking.pickInView(viewId, screen, options)
  }

  private pickAtViewScreenAsync(
    viewId: ViewId,
    screen: THREE.Vector2,
    options: Partial<PickQuery> = {}
  ): Promise<PickResult[]> {
    this.assertNotDisposed()
    return this.picking.pickInViewAsync(viewId, screen, options)
  }

  private createScopedDomElementForView(viewId: ViewId, source: HTMLElement): HTMLElement {
    this.assertNotDisposed()
    return createViewScopedDomElement(source, () => {
      const snapshot = this.views.get(viewId)?.getSnapshot()
      return snapshot?.rect ?? { x: 0, y: 0, width: 0, height: 0 }
    })
  }

  /**
   * 截图当前主视口并编码为图像 Blob。
   *
   * @param options - 截图输出参数，含目标宽高、MIME 类型与质量
   * @returns 图像数据 Blob
   */
  async screenshot(options?: ScreenshotOptions): Promise<Blob> {
    this.assertNotDisposed()
    const graphics = this.context.graphics
    if (!graphics.capabilities.screenshot) {
      throw new UnsupportedGraphicsCapabilityError('screenshot', graphics.kind)
    }
    const canvas = graphics.canvas
    const mimeType = normalizeScreenshotMimeType(options?.mimeType)
    const composite = options?.composite
    const useWorkerEncoding = options?.workerEncoding === true && supportsScreenshotWorkerEncoding()
    const targetWidth = normalizeScreenshotDimension(options?.width, canvas.width)
    const targetHeight = normalizeScreenshotDimension(options?.height, canvas.height)
    const needsCompositeCanvas =
      graphics.kind === 'webgpu' ||
      Boolean(composite) ||
      useWorkerEncoding ||
      targetWidth !== canvas.width ||
      targetHeight !== canvas.height

    this.loop.renderOnce()
    if (!needsCompositeCanvas) {
      return this.dataUrlToBlob(canvas.toDataURL(mimeType, options?.quality), mimeType)
    }

    const compositeCanvas = document.createElement('canvas')
    compositeCanvas.width = targetWidth
    compositeCanvas.height = targetHeight
    const compositeContext = compositeCanvas.getContext('2d')
    if (!compositeContext) {
      throw new Error('截图合成画布创建失败')
    }
    compositeContext.drawImage(canvas, 0, 0, targetWidth, targetHeight)
    try {
      if (composite) {
        await composite({
          context: compositeContext,
          width: compositeCanvas.width,
          height: compositeCanvas.height,
        })
      }
      if (useWorkerEncoding) {
        try {
          return await encodeCanvasInWorker(compositeCanvas, {
            mimeType,
            quality: options?.quality,
            workerBaseUrl: options?.workerBaseUrl,
          })
        } catch {
          // Worker 不可用时回退到主线程编码。
        }
      }
      return await this.canvasToBlob(compositeCanvas, mimeType, options?.quality)
    } finally {
      compositeCanvas.width = 1
      compositeCanvas.height = 1
    }
  }

  private canvasToBlob(
    canvas: HTMLCanvasElement,
    mimeType: string,
    quality?: number
  ): Promise<Blob> {
    if (typeof canvas.toBlob !== 'function') {
      return Promise.resolve(this.dataUrlToBlob(canvas.toDataURL(mimeType, quality), mimeType))
    }
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('截图编码失败'))
          }
        },
        mimeType,
        quality
      )
    })
  }

  private dataUrlToBlob(dataUrl: string, fallbackMimeType: string): Blob {
    const separator = dataUrl.indexOf(',')
    if (separator < 0) {
      throw new Error('截图失败')
    }

    const metadata = dataUrl.slice(5, separator)
    const mimeType = metadata.split(';', 1)[0] || fallbackMimeType
    const binary = atob(dataUrl.slice(separator + 1))
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i)
    }
    return new Blob([bytes], { type: mimeType })
  }

  /**
   * 设置场景背景色。
   *
   * @param color - 目标颜色，支持 Color、颜色字符串；传 null 时置空
   */
  setBackground(color: THREE.Color | THREE.Texture | string | null): void {
    this.assertNotDisposed()
    if (color === null) {
      this.context.sceneGraph.setBackground(null)
    } else if (typeof color === 'string') {
      this.context.sceneGraph.setBackground(new THREE.Color(color))
    } else {
      this.context.sceneGraph.setBackground(color)
    }
    this.context.renderInvalidation.invalidate('scene.background')
  }

  /**
   * 设置点预算。
   *
   * @param value - 全局点预算上限，≥0
   */
  setPointBudget(value: number): void {
    this.assertNotDisposed()
    this.context.pointBudget = value
    this.context.renderInvalidation.invalidate('pointcloud.pointBudget')
  }

  /**
   * 读取当前全局点预算。
   *
   * @returns 当前生效的点预算上限
   */
  getPointBudget(): number {
    this.assertNotDisposed()
    return this.context.pointBudget
  }

  /**
   * 设置最大并发加载节点数。
   *
   * @param value - 节点并发上限，≥1
   */
  setMaxNodesLoading(value: number): void {
    this.assertNotDisposed()
    this.context.maxNodesLoading = value
    this.context.renderInvalidation.invalidate('pointcloud.maxNodesLoading')
  }

  // ==================== 日志运行时控制 ====================

  /** 引擎 root logger，宿主可用 logger.child(ns) 派生命名空间实例 */
  get logger(): ILogger {
    return this.context.logger
  }

  /**
   * 设置全局日志级别。
   *
   * @param level - 目标级别，支持枚举或 'silent' | 'error' | 'warn' | 'info' | 'debug'
   */
  setLogLevel(level: LogLevelName): void {
    getLoggerManager().setLevel(level)
  }

  /**
   * 设置命名空间级别覆盖，前缀匹配生效（如 'gsplat' 连带 gsplat:* 子模块）。
   *
   * @param namespace - 命名空间
   * @param level - 目标级别，支持枚举或字符串别名
   */
  setNamespaceLevel(namespace: string, level: LogLevelName): void {
    getLoggerManager().setNamespaceLevel(namespace, level)
  }

  /**
   * 开关性能埋点。
   *
   * @param enabled - 是否启用 perf
   */
  setPerfEnabled(enabled: boolean): void {
    getLoggerManager().setPerfEnabled(enabled)
  }

  /**
   * 运行期替换日志输出 sink，立即对所有命名空间 Logger 生效。
   *
   * 宿主一般通过 EngineConfig.logging.sink 在初始化阶段注入；此方法用于拿到 engine 实例后
   * 才接管日志输出的场景。
   *
   * @param sink - 新的日志 sink
   */
  setLogSink(sink: ILogSink): void {
    getLoggerManager().setSink(sink)
  }

  /**
   * 获取底层 WebGLRenderer。
   *
   * @returns 引擎共享的 WebGLRenderer 实例
   */
  getRenderer(): THREE.WebGLRenderer {
    this.assertNotDisposed()
    if (this.context.graphics.kind !== 'webgl2') {
      throw new BackendMismatchError(
        'webgl2',
        this.context.graphics.kind,
        'PotreeEngine.getRenderer'
      )
    }
    return this.context.renderer
  }

  /** 返回不含 backend mutator、device/context/renderer 的只读图形信息。 */
  getGraphicsInfo(): ReadonlyGraphicsInfo {
    this.assertNotDisposed()
    return this.context.graphics.getInfo()
  }

  /**
   * 创建 backend-owned 区域采集 session。
   *
   * @returns 可用的采集 session；当前 backend 未实现时返回 null
   */
  createRegionCaptureSession(): GraphicsRegionCaptureSession | null {
    this.assertNotDisposed()
    return this.graphicsBackend?.createRegionCaptureSession?.() ?? null
  }

  /**
   * 获取底层 Scene。
   *
   * @returns 引擎主场景
   */
  getScene(): THREE.Scene {
    this.assertNotDisposed()
    return this.context.sceneGraph.mainScene
  }

  /**
   * 启用性能统计面板，挂载到指定容器；传 null 禁用。
   *
   * @param container - 承载 Stats DOM 的容器，传 null 卸载面板
   */
  enableStats(container: HTMLElement | null): void {
    this.assertNotDisposed()
    this.loop.enableStats(container)
  }

  /** 委托 loop.syncViewport() 同步视口尺寸 */
  resize(): void {
    this.assertNotDisposed()
    this.loop.syncViewport()
  }

  /**
   * 销毁引擎。严格按 6 步顺序执行：
   * 1. RenderLoop.dispose() — 停止帧循环并释放 ResizeObserver
   * 2. PluginManager.destroy() — 按 priority 降序逐插件 onDestroy()
   * 3. EngineContext.dispose() — 释放 DisposableScope 与 SceneGraph
   * 4. GraphicsBackend.dispose() — 释放 renderer、context 与 canvas
   * 5. EventBus.emit('engine.disposed') — 广播销毁完成事件
   * 6. EventBus.dispose() — 释放事件总线自身
   */
  dispose(): void {
    if (this._disposed) return
    const disposeStartedAt = memoryLog.isEnabled(LogLevel.INFO) ? getLoggerManager().now() : 0
    this._disposed = true

    let firstError: unknown
    const runPhase = (phase: string, action: () => void): void => {
      try {
        this._runMemoryDiagnosedDisposePhase(phase, action)
      } catch (error) {
        firstError ??= error
      }
    }

    runPhase('toolsAndSessions', () => {
      // 停用所有活跃工具（单个失败不阻塞后续销毁）
      const toolPlugins = this.pluginManager.getToolPlugins()
      this.activeTools.forEach((name, group) => {
        const tool = toolPlugins.find((t) => t.name === name)
        if (tool?.isActive) {
          try {
            tool.deactivate(this.activationToken)
            this.events.emit('tool.deactivated', { name, group })
          } catch (err) {
            this.events.emit('engine.error', {
              error: err instanceof Error ? err : new Error(String(err)),
              context: `dispose:deactivateTool:${name}`,
            })
          }
        }
      })
      this.activeTools.clear()

      // 清理残留 session（正常路径已在 tool.deactivated 中清理，此处为兜底）
      for (const entry of this.activeSessions.values()) {
        entry.invalidate()
      }
      this.activeSessions.clear()
    })

    // 任一阶段失败后仍继续后续步骤，保证 Renderer/Canvas 与宿主引用一定被释放。
    runPhase('loop', () => this.loop.dispose())
    runPhase('plugins', () => this.pluginManager.destroy())
    runPhase('context', () => this.context.dispose())
    runPhase('graphics', () => this.disposeGraphics())
    runPhase('disposedEvent', () => this.events.emit('engine.disposed', undefined))
    runPhase('eventBus', () => this.events.dispose())

    // engine.disposed 监听者执行完后再清 Plugin 查询缓存，保持事件期间的既有可观察语义。
    runPhase('pluginReferences', () => this.pluginManager.releaseReferences?.())
    runPhase('hostReferences', () => this.context.releaseHostReferences?.())
    this._logMemorySummary(
      'engine.disposed',
      disposeStartedAt > 0 ? getLoggerManager().now() - disposeStartedAt : undefined,
      firstError === undefined ? 'ok' : 'failed'
    )

    if (firstError !== undefined) {
      throw firstError
    }
  }

  private _runMemoryDiagnosedDisposePhase(phase: string, action: () => void): void {
    const startedAt = getLoggerManager().now()
    try {
      action()
    } catch (error) {
      const errorName = error instanceof Error ? error.name : typeof error
      runMemoryDiagnostic(() => {
        memoryLog.error(
          `event=dispose.phase.failed engineId=${this.memoryDiagnosticEngineId} phase=dispose.${phase}.failed scope=engine elapsedMs=${(getLoggerManager().now() - startedAt).toFixed(1)} failedPhase=dispose.${phase} errorName=${errorName}`
        )
      })
      throw error
    }
  }

  private _logMemorySummary(event: string, elapsedMs?: number, status = 'ok'): void {
    if (!memoryLog.isEnabled(LogLevel.INFO)) {
      return
    }
    try {
      const renderer = this.context.getRendererIfAvailable
        ? this.context.getRendererIfAvailable()
        : (this.context.renderer as THREE.WebGLRenderer | undefined)
      const rendererInfo = renderer?.info
      let sceneObjects = 0
      const scene = this.context.sceneGraph?.mainScene
      if (typeof scene?.traverse === 'function') {
        scene.traverse(() => {
          sceneObjects++
        })
      }
      let contextLost = false
      try {
        contextLost = renderer?.getContext?.().isContextLost?.() ?? false
      } catch {
        contextLost = false
      }
      memoryLog.info(
        `event=${event} engineId=${this.memoryDiagnosticEngineId} phase=runtime scope=engine status=${status} elapsedMs=${elapsedMs?.toFixed(1) ?? '0'} loopRunning=${this.loop.running ?? false} plugins=${this.plugins.getNames?.().length ?? 0} sceneObjects=${sceneObjects} threeGeometries=${rendererInfo?.memory?.geometries ?? 0} threeTextures=${rendererInfo?.memory?.textures ?? 0} threePrograms=${rendererInfo?.programs?.length ?? 0} canvasConnected=${renderer?.domElement?.isConnected ?? false} contextLost=${contextLost}`
      )
    } catch {
      // 摘要失败不追加日志，避免诊断自身制造噪声。
    }
  }

  /**
   * 释放引擎在 createPotreeEngine 中自建的 WebGLRenderer、WebGL context 及其 canvas。
   * renderer.dispose() 释放 Three.js 资源后，再通过独立 API forceContextLoss() 释放底层上下文。
   * 单步失败不阻塞后续销毁。
   */
  private disposeGraphics(): void {
    this.graphicsBackend?.dispose()
  }

  /** 获取便捷服务，缺失 Plugin 时抛出明确错误 */
  private requireService<K extends keyof ServiceMap>(
    key: K,
    getterName: string,
    pluginName: string
  ): ServiceMap[K] {
    this.assertNotDisposed()
    if (!this.context.hasService(key)) {
      throw new Error(
        'engine.' +
          getterName +
          ' requires ' +
          pluginName +
          ', but it is not registered. Add ' +
          pluginName +
          ' to your plugin list or use a preset that includes it.'
      )
    }
    return this.context.getService(key)
  }

  /** 断言引擎未销毁，已销毁时抛错 */
  private assertNotDisposed(): void {
    if (this._disposed) {
      throw new Error('PotreeEngine 已销毁，禁止继续操作')
    }
  }

  private disposeLateLoadedAsset(service: unknown, handle: AssetHandle): void {
    const disposableService = service as { dispose?: (id?: AssetHandle['id']) => void }
    try {
      disposableService.dispose?.(handle.id)
    } catch {
      // Best-effort cleanup after engine disposal; keep the lifecycle error as the primary signal.
    }
  }
}
